import z from 'zod';

import { OperationTypes, OperationTypesEnum } from '../constants/singleFlow.js';
import type { ApproverEntity } from '../types/shared.js';
import {
  ApprovalSequenceType,
  EntityGroupType,
  LogicalGroupType,
  type ApproverItemInput,
  type FlowFormInput,
  type FlowItem,
} from '../types/singleFlow.js';
import { parseUTCDate } from './transformers.js';

export const convertToApprovalSequence = (
  items: ApproverItemInput[],
): ApprovalSequenceType => {
  if (items.length === 0) {
    return [];
  }

  const sequence: ApprovalSequenceType = [];
  let currentLogicalGroup: LogicalGroupType = [];
  let currentEntityGroup: EntityGroupType = [];

  const actions: Record<OperationTypes, () => void> = {
    [OperationTypesEnum.enum.or]: () => {
      return;
    },
    [OperationTypesEnum.enum.and]: () => {
      currentLogicalGroup.push([...currentEntityGroup]);
      currentEntityGroup = [];
    },
    [OperationTypesEnum.enum.then]: () => {
      currentLogicalGroup.push([...currentEntityGroup]);
      sequence.push([...currentLogicalGroup]);
      currentLogicalGroup = [];
      currentEntityGroup = [];
    },
  };

  items.forEach((item) => {
    if (item.operation) {
      actions[item.operation]();
    }

    currentEntityGroup.push({
      value: item.entityId ?? '',
      label: item.entityLabel ?? '',
    });
  });

  if (currentEntityGroup.length > 0) {
    currentLogicalGroup.push(currentEntityGroup);
  }

  if (currentLogicalGroup.length > 0) {
    sequence.push(currentLogicalGroup);
  }

  return sequence;
};

const mapApproversToForm = (
  approvers: ApproverEntity[],
): FlowFormInput['approvers'] => {
  const result: FlowFormInput['approvers'] = [];

  let pendingOperation: OperationTypes | null = null;

  for (const item of approvers) {
    if (item.type === 'operation') {
      pendingOperation = OperationTypesEnum.parse(item.label);
      continue;
    }

    result.push({
      entityId: item.id,
      entityLabel: item.label,
      type: item.type,
      operation: result.length === 0 ? null : pendingOperation,
    });
    pendingOperation = null;
  }

  return result;
};

export const getFormValuesFromFlowData = (
  flowData: FlowItem,
): FlowFormInput => {
  const requesterType = flowData.requesters[0].type;

  if (requesterType === 'operation') {
    throw new Error('Type "operation" is not supported for the requesters');
  }

  return {
    name: flowData.name,
    type: flowData.flowType,
    requesterType,
    requesterValue: flowData.requesters.map((item) => {
      return {
        value: item.id,
        label: item.label,
      };
    }),
    approvers: mapApproversToForm(flowData.approvers),
    pendingRequestNotification: flowData.pendingRequestNotification,
    approvedRequestNotification: {
      ...flowData.approvedRequestNotification,
      recipientEmails:
        flowData.approvedRequestNotification.recipientEmails.join(', '),
    },
    dateFrom: parseUTCDate(flowData.dateFrom),
    dateTo: flowData.dateTo ? parseUTCDate(flowData.dateTo) : null,
  };
};

export const isStringAListOfEmails = (value: string): boolean => {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return true;
  }

  const emails = trimmedValue.split(',').map((email) => email.trim());
  return emails.every((email) => z.email().safeParse(email).success);
};
